import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n";
import Header from "@/components/Header";
import { SiteBrand } from "@/components/SiteBrand";
import { InfraAIWordmark, InfraAIText } from "@/components/InfraAIWordmark";
import LiveEngine from "@/components/LiveEngine";
import useReveal from "@/hooks/useReveal";
import useLiveStatCounter from "@/hooks/useLiveStatCounter";
import useCountUp from "@/hooks/useCountUp";
import { HERO_VIDEO_SRC, ABOUT_VIDEO_SRC } from "@/config/heroVideo";
import { GAP_ICONS, WHY_NEON_ICONS, CAPABILITY_NEON_ICONS, TRUST_NEON_ICONS } from "@/config/neonIcons";
import { PARTNER_LOGOS, partnerLogoUrl } from "@/config/partnerLogos";
import {
  Server,
  Network,
  ArrowRight,
  Compass,
  Wrench,
  Headphones,
  GraduationCap,
  Sparkles,
  Brain,
  Award,
  Building2,
  Mail,
  Phone,
  Globe,
  ChevronDown,
} from "lucide-react";

const ICON_MAP = {
  Compass, Wrench, Headphones, GraduationCap, Sparkles, Brain,
};

/** Alternating card surface tones — veridata-style banded cards */
const CARD_TONES = ["bg-card-a", "bg-card-b", "bg-card-c"];
const cardTone = (i) => CARD_TONES[i % CARD_TONES.length];

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="block h-px w-8 bg-[hsl(var(--accent-cyan)/0.55)]" />
    <span className="label-eyebrow"><InfraAIText>{children}</InfraAIText></span>
  </div>
);

const Section = ({ id, children, className = "" }) => {
  const ref = useReveal();
  return (
    <section id={id} ref={ref} className={`relative py-24 md:py-32 ${className}`}>
      {children}
    </section>
  );
};

const HERO_TEXT_START_S = 2;
const HERO_SLIDE_COUNT = 4;
const HERO_TEXT_END_PAD_S = 0.4;
/** Slayt süreleri — uzun / okunması zor slaytlar daha uzun kalır */
const HERO_SLIDE_WEIGHTS = [1, 1.6, 1.55, 1.9];

const getHeroSlideIndex = (currentTime, duration) => {
  if (!Number.isFinite(duration) || duration <= 0 || currentTime < HERO_TEXT_START_S) return -1;
  const window = Math.max(duration - HERO_TEXT_START_S - HERO_TEXT_END_PAD_S, 1);
  const elapsed = Math.min(currentTime - HERO_TEXT_START_S, window);
  const totalWeight = HERO_SLIDE_WEIGHTS.reduce((a, b) => a + b, 0);
  let acc = 0;
  for (let i = 0; i < HERO_SLIDE_WEIGHTS.length; i++) {
    acc += (HERO_SLIDE_WEIGHTS[i] / totalWeight) * window;
    if (elapsed < acc || i === HERO_SLIDE_WEIGHTS.length - 1) return i;
  }
  return HERO_SLIDE_COUNT - 1;
};

const heroSlideMotion = {
  initial: { opacity: 0, y: 36, x: -32, filter: "blur(8px)" },
  animate: { opacity: 1, y: 0, x: 0, filter: "blur(0px)" },
  exit: { opacity: 0, y: -28, x: 24, filter: "blur(6px)" },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const heroBrandMotion = {
  initial: { opacity: 0, scale: 0.92, filter: "blur(8px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, scale: 0.96, filter: "blur(4px)" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const heroSlideClass = () =>
  "hero-cinematic-headline max-w-5xl mx-auto text-center";

function HeroLogoWordmark({ className = "" }) {
  return <InfraAIWordmark variant="hero" className={`hero-cinematic-block ${className}`.trim()} />;
}

function HeroSlideParts({ parts }) {
  return parts.map((part, i) => (
    <span key={i} className={part.cyan ? "hero-cinematic-accent" : "text-white"}>
      {part.text}
    </span>
  ));
}

function HeroSlide({ slideIndex, t }) {
  const parts = t.hero.slideParts?.[slideIndex];

  return (
    <motion.div
      {...heroSlideMotion}
      className={`hero-cinematic-block w-full ${heroSlideClass()}`}
    >
      {parts ? <HeroSlideParts parts={parts} /> : t.hero.slides[slideIndex]}
    </motion.div>
  );
}

function HeroBrandScreen({ t }) {
  return (
    <motion.div
      {...heroBrandMotion}
      className="hero-cinematic-block w-full text-center"
    >
      <HeroLogoWordmark className="justify-center" />
      <p className="mt-6 font-mono text-xs sm:text-sm tracking-[0.18em] uppercase text-cyan max-w-3xl mx-auto leading-relaxed">
        <span className="text-white/35">// </span>
        {t.hero.brandTagline}
      </p>
    </motion.div>
  );
}

function Hero() {
  const { t } = useI18n();
  const [slideIndex, setSlideIndex] = React.useState(-1);
  const [videoEnded, setVideoEnded] = React.useState(false);
  const videoRef = React.useRef(null);

  const showHeroText = React.useCallback(() => {
    setVideoEnded((prev) => {
      if (prev) return prev;
      const v = videoRef.current;
      if (v) v.pause();
      return true;
    });
  }, []);

  const handleVideoEnded = () => {
    showHeroText();
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
    setSlideIndex(getHeroSlideIndex(v.currentTime, v.duration));
    if (v.currentTime >= v.duration - 0.35) showHeroText();
  };

  return (
    <section
      id="top"
      className="relative min-h-screen overflow-hidden"
      data-testid="hero"
    >
      <div className={`hero-video-wrap${videoEnded ? " hero-video-wrap--ended" : ""}`}>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          onEnded={handleVideoEnded}
          onTimeUpdate={handleTimeUpdate}
        >
          <source src={HERO_VIDEO_SRC} type="video/mp4" />
        </video>
        <div
          aria-hidden
          className="hero-video-scrim"
        />
        <div
          aria-hidden
          className={`hero-cinematic-scrim${slideIndex >= 0 && !videoEnded ? " hero-cinematic-scrim--active" : ""}`}
        />
      </div>

      <div className="absolute inset-0 z-10 flex items-center justify-center px-5 md:px-12 pointer-events-none">
        <div className="w-full max-w-6xl text-center min-h-[1px]">
          <AnimatePresence mode="wait">
            {videoEnded ? (
              <HeroBrandScreen key="brand" t={t} />
            ) : (
              slideIndex >= 0 && (
                <HeroSlide key={slideIndex} slideIndex={slideIndex} t={t} />
              )
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-pulse-glow pointer-events-none">
        <span className="font-mono text-[9px] tracking-widest2 text-white/30">SCROLL</span>
        <ChevronDown size={16} className="text-white/30" />
      </div>
    </section>
  );
}

const whyStagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.08 },
  },
};

const whyItem = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const WHY_TONE_CLASS = {
  dim: "why-t-dim",
  cyan: "why-t-cyan",
  amber: "why-t-amber",
  violet: "why-t-violet",
  green: "why-t-green",
  red: "why-t-red",
  arrow: "why-t-arrow",
  base: "",
};

function WhyEvidence({ lines }) {
  return (
    <div className="why-term">
      <div className="why-term-box">
        {lines.map((line, li) => (
          <React.Fragment key={li}>
            {li > 0 && <br />}
            {line.map((seg, si) => (
              <span
                key={si}
                className={[WHY_TONE_CLASS[seg.tone] || "", seg.tag ? "font-semibold" : ""].filter(Boolean).join(" ")}
              >
                {seg.t}
              </span>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const WHY_CHAIN_CYCLE_MS = 2800;

function WhyChain({ chain }) {
  const [active, setActive] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (paused || reduced) return undefined;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % chain.length);
    }, WHY_CHAIN_CYCLE_MS);
    return () => clearInterval(id);
  }, [paused, chain.length]);

  return (
    <motion.div
      className="why-chain mb-3.5"
      variants={whyStagger}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {chain.map((step, i) => (
        <motion.div
          key={step.n}
          variants={whyItem}
          className={[
            "why-chain-step",
            step.rival ? "why-chain-step--rival" : "why-chain-step--nai",
            i === active ? "why-chain-step--active" : "",
          ].join(" ")}
          aria-current={i === active ? "step" : undefined}
        >
          <span className="font-mono text-xs block mb-3.5">{step.n}</span>
          <div className="why-chain-verb">{step.verb}</div>
          <p className="text-[13px] leading-snug text-ink-mute m-0">{step.desc}</p>
          {step.rivalLabel && <span className="why-rival-label">{step.rivalLabel}</span>}
        </motion.div>
      ))}
    </motion.div>
  );
}

function WhyInfraAI() {
  const { t } = useI18n();
  const w = t.whyInfraAI;

  return (
    <Section id="why-infraai" className="why-infraai border-t border-line/20 overflow-hidden">
      <div aria-hidden className="why-infraai-glow" />
      <div className="container-x relative z-[1] max-w-[1080px]">
        <motion.div
          className="max-w-[620px]"
          variants={whyStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
        >
          <motion.div variants={whyItem} className="why-eyebrow">
            <span className="why-eyebrow-dot" aria-hidden />
            <InfraAIText>{w.eyebrow}</InfraAIText>
          </motion.div>
          <motion.h2
            variants={whyItem}
            className="text-[clamp(2.375rem,6vw,4.125rem)] tracking-tight font-extrabold leading-[1.02] mt-0 mb-6"
          >
            {w.title}
            <InfraAIWordmark variant="inline" suffix={w.titleSuffix} />
          </motion.h2>
          <motion.p
            variants={whyItem}
            className="text-[clamp(1rem,1.7vw,1.1875rem)] leading-relaxed text-ink-mute mb-14"
          >
            {w.thesisParts.map((part, i) => (
              <span
                key={i}
                className={
                  part.cyan ? "text-cyan font-semibold" : part.bold ? "text-ink font-semibold" : undefined
                }
              >
                <InfraAIText>{part.text}</InfraAIText>
              </span>
            ))}
          </motion.p>
        </motion.div>

        <WhyChain chain={w.chain} />
        <div aria-hidden className="why-flowline mb-14" />

        <motion.div
          className="why-pillars"
          variants={whyStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {w.pillars.map((p) => {
            const iconSrc = WHY_NEON_ICONS[p.icon];
            return (
              <motion.div key={p.k} variants={whyItem} className="why-pillar">
                <div className="neon-icon-wrap why-pillar-icon">
                  {iconSrc && (
                    <img
                      src={iconSrc}
                      alt=""
                      loading="lazy"
                      draggable={false}
                    />
                  )}
                </div>
                <h3 className="font-heading text-lg font-bold tracking-tight mb-2">{p.k}</h3>
                <p className="why-pillar-body text-[13.5px] leading-snug text-ink-mute m-0">{p.v}</p>
                {p.evidence && <WhyEvidence lines={p.evidence} />}
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          className="why-stat-row mt-11"
          variants={whyItem}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.5 }}
        >
          {w.stats.map((s, i) => (
            <React.Fragment key={s.k}>
              {i > 0 && <span className="why-stat-div hidden sm:block" aria-hidden />}
              <div className="font-heading text-[22px] font-extrabold tracking-tight text-ink">
                {s.v} <span className="text-cyan">{s.k}</span>
              </div>
            </React.Fragment>
          ))}
          <span className="text-[13px] text-ink-dim font-body">{w.statsTail}</span>
        </motion.div>
      </div>
    </Section>
  );
}

function MonitoringGap() {
  const { t } = useI18n();
  return (
    <Section id="gap" className="border-t border-line/20 bg-bg-section">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.gap.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold max-w-4xl leading-tight mt-3">
            <InfraAIText wordmarkClassName="font-bold">{t.gap.title}</InfraAIText>
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 stagger">
          {t.gap.cards.map((c, i) => (
              <div
                key={c.k}
                className={`reveal card-premium ${cardTone(i)} border border-line/65 p-8 md:p-10`}
              >
                <div className="neon-icon-wrap mb-6 h-[4.5rem] w-[4.5rem]">
                  <img
                    src={GAP_ICONS[i]}
                    alt=""
                    className="max-h-full max-w-full object-contain object-left-bottom"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
                <div className="font-heading text-xl tracking-tight mb-3">{c.k}</div>
                <p className="text-ink-mute leading-relaxed text-[15px]"><InfraAIText>{c.v}</InfraAIText></p>
              </div>
            ))}
        </div>
      </div>
    </Section>
  );
}

function Platform() {
  const { t } = useI18n();
  return (
    <Section id="platform" className="border-t border-line/20 bg-bg-section">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.platform.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold max-w-4xl leading-tight mt-3">
            {t.platform.title}
          </h2>
          {t.platform.tagline && (
            <p className="mt-4 text-xl md:text-2xl tracking-tight font-semibold text-ink/90 max-w-4xl leading-snug">
              {t.platform.tagline}
            </p>
          )}
          {t.platform.sub && (
            <p className="mt-5 text-ink-mute max-w-3xl text-lg leading-relaxed"><InfraAIText>{t.platform.sub}</InfraAIText></p>
          )}
        </div>

        <div className="reveal mt-12 border border-cyan/20 overflow-hidden" style={{ boxShadow: "0 0 60px rgba(0,212,255,0.06)" }}>
          <div className="border-b border-line/60 px-5 py-3 flex items-center gap-3 bg-bg-paper/60">
            <span className="w-2 h-2 rounded-full bg-cyan animate-pulse-glow" />
            <span className="font-mono text-[10px] tracking-widest2 text-ink-mute">LIVE · AI ENGINE ORCHESTRATION</span>
          </div>
          <LiveEngine className="w-full" />
        </div>
      </div>
    </Section>
  );
}

const AUTO_CYCLE_MS = 3000;

function RcaFlow() {
  const { t } = useI18n();
  const [active, setActive] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [codeVisible, setCodeVisible] = React.useState(true);
  const timerRef = React.useRef(null);
  const steps = t.rca.steps;

  /* Progress bar width per step */
  const [progress, setProgress] = React.useState(0);
  const progressRef = React.useRef(null);

  const goTo = React.useCallback((idx) => {
    setCodeVisible(false);
    setTimeout(() => {
      setActive(idx);
      setProgress(0);
      setCodeVisible(true);
    }, 180);
  }, []);

  /* Auto-cycle */
  React.useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => {
      setActive((prev) => {
        const next = (prev + 1) % steps.length;
        setCodeVisible(false);
        setTimeout(() => { setCodeVisible(true); setProgress(0); }, 180);
        return next;
      });
    }, AUTO_CYCLE_MS);
    return () => clearInterval(interval);
  }, [paused, steps.length]);

  /* Progress animation */
  React.useEffect(() => {
    if (paused) return;
    setProgress(0);
    const start = Date.now();
    const raf = () => {
      const pct = Math.min(((Date.now() - start) / AUTO_CYCLE_MS) * 100, 100);
      setProgress(pct);
      if (pct < 100) progressRef.current = requestAnimationFrame(raf);
    };
    progressRef.current = requestAnimationFrame(raf);
    return () => cancelAnimationFrame(progressRef.current);
  }, [active, paused]);

  return (
    <Section id="rca" className="border-t border-line/40">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.rca.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold max-w-4xl leading-tight">
            {t.rca.titleLead}
            <InfraAIWordmark variant="inline" className="font-bold" />
            {" "}
            {t.rca.title}
          </h2>
          <p className="mt-5 text-ink-mute max-w-2xl text-lg">{t.rca.sub}</p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-8"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* stepper */}
          <div className="reveal-left lg:col-span-5 space-y-3">
            {steps.map((s, i) => (
              <button
                key={s.n}
                onClick={() => { setPaused(true); goTo(i); }}
                data-testid={`rca-step-${i}`}
                className={`w-full text-left border p-6 transition-all duration-300 relative overflow-hidden ${
                  i === active
                    ? "border-cyan/50 bg-bg-paper"
                    : "border-line hover:border-line/80 bg-transparent"
                }`}
              >
                {/* Progress bar */}
                {i === active && !paused && (
                  <span
                    className="absolute bottom-0 left-0 h-[2px] bg-cyan transition-none"
                    style={{ width: `${progress}%` }}
                  />
                )}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[11px] tracking-widest2 text-ink-dim">
                    STEP {s.n}
                  </span>
                  <span className={`w-2 h-2 rounded-full transition-colors ${
                    i === active ? "bg-cyan animate-pulse-glow" : "bg-line"
                  }`} />
                </div>
                <div className="font-heading text-xl tracking-tight mb-2">{s.k}</div>
                <p className="text-ink-mute text-[14px] leading-relaxed"><InfraAIText>{s.v}</InfraAIText></p>
              </button>
            ))}
          </div>

          {/* code panel */}
          <div className="reveal-right lg:col-span-7">
            <div className="border border-line/80 bg-bg-paper/60 h-full">
              <div className="flex items-center justify-between border-b border-line/60 px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber animate-pulse-glow" />
                  <span className="font-mono text-[10px] tracking-widest2 text-ink-mute">
                    EVIDENCE · STEP {steps[active].n}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-ink-dim">
                  reproducible · signed · scope-bounded
                </span>
              </div>
              <pre
                className="p-6 md:p-8 font-mono text-[13px] text-ink leading-relaxed overflow-x-auto whitespace-pre transition-opacity duration-200"
                style={{ opacity: codeVisible ? 1 : 0 }}
              >
                <code>{steps[active].code}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function StatCell({ v, k, sub, color = "#00D4FF", live = false }) {
  const { ref, display } = useLiveStatCounter(v, 1600, { min: 90, max: 92, enabled: live });
  return (
    <div
      ref={ref}
      className="count-cell relative p-8 md:p-10 flex flex-col justify-between group overflow-hidden border border-line/30 bg-bg-paper/60 backdrop-blur-sm h-full min-h-[220px]"
    >
      {live && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 font-mono text-[9px] tracking-widest2 text-cyan uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse-glow" aria-hidden />
          Canlı
        </span>
      )}
      <div className="absolute top-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-500" style={{ background: color }} />
      <div
        className="font-heading text-5xl md:text-6xl xl:text-7xl tracking-tighter font-black mb-4 tabular-nums"
        style={{ color }}
      >
        {display}
      </div>
      <div>
        <div className="text-ink text-[16px] font-semibold mb-1">{k}</div>
        <div className="text-ink-dim text-[12px] font-mono leading-relaxed">{sub}</div>
      </div>
    </div>
  );
}

function Metrics() {
  const { t } = useI18n();
  const COLORS = ["#00D4FF", "#F59E0B", "#9B59F5", "#10B981"];
  return (
    <Section id="metrics" className="border-t border-line/20 !py-28 relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
      <div aria-hidden className="orb orb-violet w-[500px] h-[500px] top-[-100px] right-[-150px] opacity-30" />
      <div aria-hidden className="orb orb-amber  w-[400px] h-[400px] bottom-[-100px] left-[-100px] opacity-25" />

      <div className="container-x relative">
        <div className="reveal text-center mb-16">
          <Eyebrow>{t.metrics.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold max-w-3xl mx-auto leading-tight mt-3">
            {t.metrics.title}
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger items-stretch">
          {t.metrics.stats.map((s, i) => (
            <div key={s.k} className="reveal h-full">
              <StatCell v={s.v} k={s.k} sub={s.sub} color={COLORS[i]} live={s.live} />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function InfraAIPreviewVideo({ borderless = false, loop = true, tagline = null }) {
  const wrapRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const hasStartedRef = React.useRef(false);
  const [videoEnded, setVideoEnded] = React.useState(false);
  const [shouldMountVideo, setShouldMountVideo] = React.useState(false);

  React.useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const mountObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMountVideo(true);
          mountObserver.disconnect();
        }
      },
      { rootMargin: "240px 0px", threshold: 0.01 },
    );

    mountObserver.observe(wrap);
    return () => mountObserver.disconnect();
  }, []);

  React.useEffect(() => {
    if (!shouldMountVideo) return undefined;

    const wrap = wrapRef.current;
    const video = videoRef.current;
    if (!wrap || !video) return undefined;

    const playVideo = (resetTime) => {
      if (resetTime) {
        setVideoEnded(false);
        video.currentTime = 0;
      }

      const run = () => {
        video.play().catch(() => {});
      };

      if (video.preload === "none") {
        video.preload = "auto";
      }

      if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        run();
      } else {
        video.addEventListener("canplaythrough", run, { once: true });
        video.load();
      }
    };

    const playObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!loop) {
            if (hasStartedRef.current) return;
            hasStartedRef.current = true;
            playObserver.disconnect();
            playVideo(true);
          } else {
            playVideo(false);
          }
        } else if (loop) {
          video.pause();
        }
      },
      { threshold: 0.35, rootMargin: "0px 0px 4% 0px" },
    );

    playObserver.observe(wrap);
    return () => playObserver.disconnect();
  }, [shouldMountVideo, loop]);

  const handleEnded = () => {
    if (loop) return;
    const video = videoRef.current;
    if (video) video.pause();
    setVideoEnded(true);
  };

  return (
    <div
      ref={wrapRef}
      className={`about-video-wrap h-full overflow-hidden bg-bg-card-a ${
        borderless
          ? "border-0"
          : "border border-line/65 shadow-[0_0_80px_rgba(0,212,255,0.08)]"
      }${videoEnded ? " about-video-wrap--ended" : ""}`.trim()}
    >
      {shouldMountVideo && (
        <video
          ref={videoRef}
          muted
          loop={loop}
          playsInline
          preload="none"
          disablePictureInPicture
          aria-label="InfraAI platform preview"
          className="about-video"
          onEnded={handleEnded}
        >
          <source src={ABOUT_VIDEO_SRC} type="video/mp4" />
        </video>
      )}
      <div aria-hidden className="about-video-scrim" />
      <div aria-hidden className="about-video-watermark-mask" />
      {!loop && (
        <div
          className={`about-video-end-wordmark${videoEnded ? " is-visible" : ""}`}
          aria-hidden={!videoEnded}
        >
          <InfraAIWordmark variant="inline" className="about-video-end-wordmark__brand" />
          {tagline && (
            <p className="about-video-end-wordmark__tagline">{tagline}</p>
          )}
        </div>
      )}
    </div>
  );
}

function Capabilities() {
  const { t } = useI18n();
  const c = t.capabilities;
  return (
    <Section id="capabilities" className="capabilities border-t border-line/20 overflow-hidden">
      <div aria-hidden className="use-cases-glow" />
      <div className="container-x relative z-[1]">
        <div className="capabilities-hero-grid grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          <div className="reveal capabilities-hero-card capabilities-hero-card--border-beam flex flex-col justify-center h-full">
            <div className="why-eyebrow">
              <span className="why-eyebrow-dot" aria-hidden />
              <InfraAIText>{c.eyebrow}</InfraAIText>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl tracking-tight font-bold leading-tight mt-4">
              {c.titleWordmark && (
                <>
                  <InfraAIWordmark variant="inline" className="mr-0" />
                  <span className="text-ink">:</span>
                  {" "}
                </>
              )}
              {c.title}
            </h2>
            <p className="mt-5 text-ink-mute text-base md:text-lg leading-relaxed">
              <InfraAIText>{c.sub}</InfraAIText>
            </p>
          </div>

          <div className="reveal capabilities-hero-card capabilities-hero-card--media h-full">
            <InfraAIPreviewVideo borderless loop={false} tagline={c.previewTagline} />
          </div>
        </div>

        {c.cardsTitle && (
          <div className="reveal capabilities-cards-title mt-14 mb-6">
            <InfraAIWordmark variant="inline" className="mr-[0.35em]" />
            <span>{c.cardsTitle}</span>
          </div>
        )}

        <div className="capabilities-grid mt-8 stagger">
          {c.verticals.map((v) => {
            const iconSrc = CAPABILITY_NEON_ICONS[v.icon];
            return (
              <div key={v.icon} className="reveal capability-card group h-full">
                {v.badge && (
                  <span className={`capability-badge capability-badge--${v.badgeTone || "compliance"}`}>
                    {v.badge}
                  </span>
                )}
                <div className="neon-icon-wrap capability-icon">
                  {iconSrc && (
                    <img
                      src={iconSrc}
                      alt=""
                      loading="lazy"
                      draggable={false}
                    />
                  )}
                </div>
                <h3 className="font-heading text-xl md:text-[1.35rem] font-bold tracking-tight mb-5 leading-snug">
                  {v.k}
                </h3>
                <ul className="capability-list">
                  {v.bullets.map((b) => (
                    <li key={b}>
                      <span className="capability-check" aria-hidden>✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function Trust() {
  const { t } = useI18n();
  return (
    <Section id="trust" className="border-t border-line/40 relative overflow-hidden bg-bg-section !pt-14 md:!pt-20">
      <div aria-hidden className="orb orb-cyan w-[360px] h-[360px] top-1/3 right-[-100px] opacity-30" />

      <div className="container-x relative">
        <div>
          <Eyebrow>{t.trust.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold max-w-4xl leading-tight">
            {t.trust.title}
          </h2>
          {t.trust.sub && (
            <p className="mt-5 text-ink-mute max-w-3xl text-lg leading-relaxed"><InfraAIText>{t.trust.sub}</InfraAIText></p>
          )}
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
          {t.trust.items.map((c, i) => (
              <div key={c.k} className="bg-bg-paper p-8 hover:bg-bg-surface transition-colors">
                <div className="neon-icon-wrap trust-icon mb-5 h-[4.5rem] w-[4.5rem]">
                  <img
                    src={TRUST_NEON_ICONS[i]}
                    alt=""
                    loading="lazy"
                    draggable={false}
                  />
                </div>
                <div className="font-heading text-lg tracking-tight mb-3">{c.k}</div>
                <p className="text-ink-mute text-[14px] leading-relaxed"><InfraAIText>{c.v}</InfraAIText></p>
              </div>
            ))}
        </div>
      </div>
    </Section>
  );
}

function Stack() {
  const { t } = useI18n();
  return (
    <Section id="stack" className="border-t border-line/40 !py-24 bg-bg-section">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.stack.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium max-w-3xl leading-tight">
            {t.stack.title}
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line/40 border border-line/60 stagger">
          {t.stack.groups.map((g, i) => (
            <div key={g.k} className={`reveal ${cardTone(i)} p-6`}>
              <div className="font-mono text-[10px] tracking-widest2 text-accent-amber mb-2">
                {g.k.toUpperCase()}
              </div>
              <div className="font-mono text-[13px] text-ink leading-relaxed">
                {g.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function ContactCTA() {
  const { t } = useI18n();
  const c = t.contact;
  const mailHref = `mailto:${c.email}?subject=${encodeURIComponent("InfraAI Demo Request")}`;

  return (
    <Section id="contact" className="border-t border-line/40">
      <div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="reveal-left lg:col-span-5">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium leading-tight">
            <InfraAIText>{c.title}</InfraAIText>
          </h2>
          <p className="mt-5 text-ink-mute text-[15px] leading-relaxed"><InfraAIText>{c.sub}</InfraAIText></p>

          {c.trustBand?.length > 0 && (
            <ul className="form-trust-band mt-10 space-y-5">
              {c.trustBand.map((item) => (
                <li key={item.k} className="form-trust-band__item">
                  <span className="form-trust-band__check" aria-hidden>✓</span>
                  <span>
                    <strong className="block text-ink font-semibold text-[14px] mb-1">{item.k}</strong>
                    <span className="text-[14px] leading-relaxed"><InfraAIText>{item.v}</InfraAIText></span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="reveal-right lg:col-span-7">
          <div
            className="contact-cta-panel border border-line/80 bg-bg-paper/40 p-6 md:p-10"
            data-testid="contact-cta"
          >
            <div className="font-mono text-[10px] tracking-widest2 text-ink-dim uppercase mb-6">
              {c.emailLabel}
            </div>

            <a
              href={mailHref}
              className="inline-flex items-center gap-2 bg-cyan text-black font-mono text-[12px] tracking-widest2 uppercase px-6 py-3.5 hover:bg-cyan-light transition-colors"
              data-testid="contact-email-cta"
            >
              {c.ctaEmail}
              <ArrowRight size={14} />
            </a>

            <div className="mt-8">
              <a
                href={mailHref}
                className="contact-cta-link group inline-flex items-center gap-3 text-ink hover:text-cyan transition-colors"
              >
                <Mail size={16} className="text-cyan shrink-0" strokeWidth={1.5} />
                <span className="font-mono text-[14px]">{c.email}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ---------- About (Hakkımızda) ---------- */
function AboutStatValue({ value }) {
  const { ref, display } = useCountUp(value, 1600);
  return (
    <div ref={ref} className="font-heading text-3xl tracking-tighter text-cyan font-black tabular-nums">
      {display}
    </div>
  );
}

function About() {
  const { t } = useI18n();
  return (
    <Section id="about" className="border-t border-line/20 overflow-hidden">
      <div aria-hidden className="orb orb-cyan w-[500px] h-[500px] top-[-150px] right-[-100px] opacity-20" />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 xl:gap-16 items-stretch">
          {/* Sol — metin */}
          <div className="reveal flex flex-col justify-center min-h-[280px] lg:min-h-[360px]">
            <Eyebrow>{t.about.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-5xl tracking-tight font-bold leading-tight mt-3">
              <InfraAIText>{t.about.title}</InfraAIText>
            </h2>
            <p className="mt-6 text-ink-mute text-lg leading-relaxed">
              <InfraAIText>{t.about.lead}</InfraAIText>
            </p>
          </div>

          {/* Sağ — InfraAI chip videosu */}
          <div className="reveal relative min-h-[280px] lg:min-h-[360px]">
            <InfraAIPreviewVideo />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          {t.about.stats.map((s, i) => (
            <div
              key={s.k}
              className={`reveal card-premium ${cardTone(i)} about-stat-card border border-line/65 p-5`}
            >
              {i < 2 ? (
                <AboutStatValue value={s.v} />
              ) : (
                <div className="font-heading text-3xl tracking-tighter text-cyan font-black">{s.v}</div>
              )}
              <div className="font-mono text-[10px] tracking-widest2 text-ink-dim uppercase mt-2 leading-relaxed">
                {s.k}
              </div>
              {s.sub && (
                <div className="font-mono text-[9px] tracking-wide text-ink-dim mt-1.5 leading-snug normal-case">
                  {s.sub}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ---------- Services (Hizmetler) ---------- */
function Services() {
  const { t } = useI18n();
  const ACCENT = ["#00D4FF", "#F59E0B", "#9B59F5", "#10B981", "#00D4FF", "#F59E0B"];
  return (
    <Section id="services" className="border-t border-line/20 bg-bg-section">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.services.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold max-w-4xl leading-tight mt-3">
            {t.services.title}
          </h2>
          <p className="mt-5 text-ink-mute max-w-2xl text-lg"><InfraAIText>{t.services.sub}</InfraAIText></p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {t.services.items.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Sparkles;
            return (
              <div key={s.k} className={`reveal card-premium ${cardTone(i)} border border-line/65 p-7`}>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 border"
                  style={{ borderColor: `${ACCENT[i]}44`, background: `${ACCENT[i]}10` }}
                >
                  <Icon size={18} strokeWidth={1.5} style={{ color: ACCENT[i] }} />
                </div>
                <div className="font-heading text-lg tracking-tight mb-2">{s.k}</div>
                <p className="text-ink-mute text-[14px] leading-relaxed"><InfraAIText>{s.v}</InfraAIText></p>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

/* ---------- Supported Stack (Desteklenen Teknolojiler) ---------- */
function Partners() {
  const { t } = useI18n();
  return (
    <Section id="partners" className="border-t border-line overflow-hidden bg-bg-section">
      <div aria-hidden className="orb orb-amber w-[400px] h-[400px] top-[-100px] left-[20%] opacity-40" />
      <div className="container-x relative">
        <div className="reveal">
          <Eyebrow>{t.partners.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold max-w-3xl leading-tight">
            {t.partners.title}
          </h2>
          <p className="mt-5 text-ink-mute max-w-3xl text-[15px] leading-relaxed"><InfraAIText>{t.partners.sub}</InfraAIText></p>
        </div>

        <div className="reveal mt-12 border border-line bg-bg-paper/60 backdrop-blur-sm">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between gap-4">
            <span className="font-mono text-[10px] tracking-widest2 text-ink-dim">{t.partners.gridLabel}</span>
            <span className="font-mono text-[10px] tracking-widest2 text-accent-cyan shrink-0">{t.partners.gridCount}</span>
          </div>
          <div className="partner-logo-grid">
            {PARTNER_LOGOS.map((logo, i) => (
              <div
                key={logo.slug}
                className={`partner-logo-cell ${cardTone(i)}`}
                title={logo.name}
              >
                <img
                  src={partnerLogoUrl(logo.slug)}
                  alt={logo.name}
                  loading="lazy"
                  draggable={false}
                  className="partner-logo-img"
                />
              </div>
            ))}
          </div>
          {t.partners.footerQuote && (
            <p className="partners-footer-quote"><InfraAIText>{t.partners.footerQuote}</InfraAIText></p>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ---------- Certificates (Sertifikalar) ---------- */
function Certificates() {
  const { t } = useI18n();
  return (
    <Section id="certificates" className="border-t border-line !py-24 bg-bg-section">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.certificates.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium max-w-3xl leading-tight">
            {t.certificates.title}
          </h2>
          <p className="mt-5 text-ink-mute max-w-2xl text-[15px]">{t.certificates.sub}</p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line stagger">
          {t.certificates.items.map((c, i) => (
            <div key={c.k} className={`reveal ${cardTone(i)} p-6 flex items-start gap-4`}>
              <Award size={18} className="text-amber mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-heading text-base tracking-tight mb-1">{c.k}</div>
                <div className="font-mono text-[11px] text-ink-mute">{c.v}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ---------- References (Referanslar) ---------- */
function References() {
  const { t } = useI18n();
  const sectorIcons = [
    "M3 21h18M4 8l8-5 8 5v13H4V8zm6 3v6m4-6v6", // bank-ish (finance)
    "M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z", // saas/star
    "M12 4v16m-8-8h16", // healthcare cross
    "M4 13l4-8h8l4 8M4 13v6h16v-6M4 13h16", // energy/factory
    "M3 21V8l9-6 9 6v13M3 21h18M9 21V11h6v10", // gov/building
    "M2 8h20M4 8v12h16V8M9 8V4h6v4", // manufacturing
  ];
  return (
    <Section id="references" className="border-t border-line overflow-hidden bg-bg-section">
      <div aria-hidden className="orb orb-cyan w-[440px] h-[440px] bottom-[-150px] left-[-100px] opacity-40" />
      <div className="container-x relative">
        <div className="reveal">
          <Eyebrow>{t.references.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium max-w-3xl leading-tight">
            {t.references.title}
          </h2>
          <p className="mt-5 text-ink-mute max-w-2xl text-[15px]">{t.references.sub}</p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line stagger">
          {t.references.sectors.map((s, i) => (
            <div key={s.k} className={`reveal ${cardTone(i)} p-7 group hover:bg-bg-surface transition-colors relative overflow-hidden`}>
              {/* corner accent */}
              <div className="absolute top-0 right-0 w-16 h-16 opacity-10 group-hover:opacity-30 transition-opacity">
                <svg viewBox="0 0 24 24" className="w-full h-full text-cyan" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d={sectorIcons[i]} />
                </svg>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Building2 size={14} className="text-cyan" strokeWidth={1.5} />
                <span className="font-mono text-[10px] tracking-widest2 text-accent-cyan">TIER-1 REFERENCE</span>
              </div>
              <div className="font-heading text-xl tracking-tight mb-2">{s.k}</div>
              <div className="font-mono text-[11px] text-ink-mute mb-4">{s.v}</div>

              <div className="flex items-center gap-1.5 text-[11px] font-mono text-ink-dim">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                <span>NDA-bound · live deployment</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ---------- Support (Destek) ---------- */
function Support() {
  const { t } = useI18n();
  const icons = [Mail, Phone, Globe, Headphones];
  return (
    <Section id="support" className="border-t border-line bg-bg-section">
      <div className="container-x">
        <div className="reveal">
          <Eyebrow>{t.support.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium max-w-3xl leading-tight">
            {t.support.title}
          </h2>
          <p className="mt-5 text-ink-mute max-w-2xl text-[15px]">{t.support.sub}</p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line stagger">
          {t.support.channels.map((c, i) => {
            const Icon = icons[i] || Headphones;
            return (
              <div key={c.k} className={`reveal ${cardTone(i)} p-6`}>
                <Icon size={18} className="text-cyan mb-4" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-widest2 text-ink-dim uppercase mb-2">
                  {c.k}
                </div>
                <div className="text-ink text-[14px] break-all">{c.v}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-line/60 py-6 md:py-8 mt-4">
      <div className="container-x flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SiteBrand />

        <div className="font-mono text-[10px] tracking-wide text-ink-dim sm:text-right shrink-0">
          <div>© {new Date().getFullYear()} InfraAI</div>
          <div className="mt-0.5">{t.footer.rights}</div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="relative overflow-x-hidden" data-testid="landing-page">
      <Header />

      {/* 1. Hero — dinamik video + giriş metinleri */}
      <Hero />

      {/* 2. Sektör yetkinlikleri — outcome odaklı dikey kartlar */}
      <Capabilities />

      {/* 3. Fark & felsefe — izleme boşluğu + neden InfraAI */}
      <MonitoringGap />
      <WhyInfraAI />

      {/* 4. Teknik çözüm — AI HUB şeması + 4 katman kartları */}
      <Platform />

      {/* 5. AI derinliği — RCA akış simülasyonu */}
      <RcaFlow />

      {/* 6. Somut kanıt & ROI — 90 gün metrikleri */}
      <Metrics />

      {/* 7. Entegrasyon & uyumluluk — ortaklar, referanslar */}
      <Partners />
      {/* <Certificates /> — geçici gizli */}
      {/* <References /> — geçici gizli */}

      {/* 8. Kurumsal sınıf özellikleri */}
      <Trust />

      {/* Stack — geçici gizli */}
      {/* <Stack /> */}

      {/* 9. Güven, ekip & destek */}
      <About />
      <Services />
      {/* <Support /> — geçici gizli */}

      {/* 10. Kapanış — iletişim CTA */}
      <ContactCTA />

      <Footer />
    </div>
  );
}
